import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@components/toast/Toast";
import api from "./api";

async function getCustomers(query) {
  const queryString = query ? `?${query}` : "";
  try {
    const response = await api.get(`api/customers${queryString}`);
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
}

async function fetchCustomers(query, { signal } = {}) {
  const queryString = query ? `?${query}` : "";
  const response = await api.get(`api/customers${queryString}`, { signal });
  return response.data;
}

async function fetchCustomersV2(query, { signal } = {}) {
  const queryString = query ? `?${query}` : "";
  const response = await api.get(`api/customers/search${queryString}`, {
    signal,
  });
  return response.data;
}

// Statistics only for several account searches in one request; each query is a search query string.
async function fetchCustomerStatistics(queries, { signal } = {}) {
  const response = await api.post("api/customers/search/statistics", { queries }, { signal });
  return response.data;
}

// The Ontik read for a working set, generated fresh on every call.
// A cohort's Ontik read and plan of action. cohortId (a working set's id, or "portfolio") keeps it for
// the day; refresh regenerates it.
async function fetchWorkingSetRead({ name, query, definition, cohortId, refresh = false }, { signal } = {}) {
  const response = await api.post(
    "api/customers/working-set/read",
    { name, query, definition, cohortId, refresh },
    { signal },
  );
  return response.data;
}

// A working set's plan of action from its accounts' stored suggestions. regenerateChanged refreshes the accounts
// that moved since their suggestion was made (slower: it may call the model).
async function fetchWorkingSetPlan({ query, regenerateChanged = false }, { signal } = {}) {
  const response = await api.post("api/customers/working-set/plan", { query, regenerateChanged }, { signal });
  return response.data;
}

// Marks account suggestions "Done" or "Dismissed" — every member of a plan step at once.
async function setAccountSuggestionStatus({ suggestionIds, status }) {
  await api.post("api/customers/account-suggestions/status", { suggestionIds, status });
}

// Marks one plan-of-action suggestion "Done" or "Dismissed".
async function setCohortSuggestionStatus({ readId, suggestionId, status }) {
  await api.post(`api/customers/working-set/read/${readId}/plan/${encodeURIComponent(suggestionId)}/status`, { status });
}

// A working set's daily overdue balance by due month (query = its search query string; empty = all accounts).
async function fetchOverdueBurndown({ query, from, to }, { signal } = {}) {
  const response = await api.post("api/customers/overdue-burndown", { query, from, to }, { signal });
  return response.data;
}

async function getCustomerById(id) {
  try {
    const response = await api.get(`api/customers/${id}`, {
      headers: {
        Accept: "application/private.json",
      },
    });
    return response.data;
  } catch (error) {
    return [];
  }
}

async function fetchCustomerById(id, { signal } = {}) {
  const { data } = await api.get(`api/customers/${id}`, {
    headers: { Accept: "application/private.json" },
    signal,
  });
  return data;
}

async function fetchLedgerSyncStatus({ signal } = {}) {
  const { data } = await api.get("api/customers/ledger-sync-status", { signal });
  return data;
}

// When the merchant's ledger was last synced from the ERP. Fetched apart from the customer list so a slow
// or unavailable ERP service never holds up the accounts table; `lastSyncedAt` is null when no sync is recorded.
function useLedgerSyncStatus(merchantId) {
  return useQuery({
    queryKey: ["ledgerSyncStatus", merchantId],
    queryFn: ({ signal }) => fetchLedgerSyncStatus({ signal }),
    enabled: !!merchantId,
    staleTime: 60_000,
    retry: false,
  });
}

function useCustomerDetails(customerId) {
  return useQuery({
    queryKey: ["customer", customerId],
    queryFn: ({ signal }) => fetchCustomerById(customerId, { signal }),
    enabled: !!customerId,
    staleTime: 60_000,
  });
}

async function getCustomerLedgerTransactions(code) {
  try {
    const response = await api.get(`api/customers/${code}/ledgertransactions`);
    return response.data;
  } catch (error) {
    return [];
  }
}

async function getCustomersCount() {
  try {
    const response = await api.get("api/customers/count");
    return response.data;
  } catch (error) {
    console.error(error);
    return 0;
  }
}

async function assignPaymentProfileToCustomers(paymentProfileId, customerIds) {
  try {
    const response = await api.patch("api/customers/payment-profile", {
      paymentProfileId,
      customerIds,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

// Save a note against a customer. The BFF routes this through the intelligence-service, which writes the
// note to customers-service and posts it to the Slack channel. conversationId ties the note back to the
// AI-chat thread it was accepted from. mentionedUserIds are passed through for the staff-notification fan-out.
async function saveSmartNote({ customerId, content, mentionedUserIds, conversationId }) {
  const { data } = await api.post("api/suggestions/notes", {
    customerId,
    content,
    mentionedUserIds,
    conversationId,
  });
  return data; // NoteDto
}

function useSaveSmartNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveSmartNote,
    onSuccess: (note, variables) => {
      toast({ description: "Note added" });

      if (!variables.customerId) return;
      const activityKey = ["customerActivities", variables.customerId];

      // Optimistic row; the realtime ActivityCreated hint reconciles the canonical feed (see CustomerContextPanel).
      queryClient.setQueryData(activityKey, (old) => {
        if (!old?.pages?.length) return old;
        const optimisticRow = {
          id: `optimistic-note-${note?.id ?? Date.now()}`,
          eventName: "NoteCreated",
          title: variables.content,
          occurredAt: new Date().toISOString(),
          taggedUsers: variables.taggedUsers ?? [],
        };
        const [firstPage, ...rest] = old.pages;
        return {
          ...old,
          pages: [
            { ...firstPage, items: [optimisticRow, ...(firstPage?.items ?? [])] },
            ...rest,
          ],
        };
      });
    },
    onError: () => {
      toast({ description: "Couldn’t save note. Please try again." });
    },
  });
}

export {
  getCustomerLedgerTransactions,
  getCustomers,
  fetchCustomers,
  fetchCustomersV2,
  fetchCustomerStatistics,
  fetchWorkingSetRead,
  setCohortSuggestionStatus,
  fetchWorkingSetPlan,
  setAccountSuggestionStatus,
  fetchOverdueBurndown,
  fetchCustomerById,
  fetchLedgerSyncStatus,
  useLedgerSyncStatus,
  useCustomerDetails,
  getCustomerById,
  getCustomersCount,
  assignPaymentProfileToCustomers,
  saveSmartNote,
  useSaveSmartNote,
};
